/**
 * The firm's packet defaults: how a filing packet's dividers and page
 * numbers look when a case hasn't set its own. Saved on the firm as
 * `default_packet_settings` (see `CASE_PREP_FIELDS`); `PacketStyle` is the
 * flat shape the Filing Packet controls edit.
 */
import type { FirmPacketSettings } from "@/api/types/lawFirm";
import {
  defaultPacketConfig,
  paginationPositionLabels,
  type DividerTabTextSize,
  type PaginationPosition
} from "@/api/types/packet";
import { defaultColors } from "@/types/packet.types";

import {
  storedPacketContents,
  storedPacketOrder,
  type PacketContentKind,
  type PacketContents
} from "./packetContents";

export type PacketStyle = {
  mainGroupEnabled: boolean;
  categoryEnabled: boolean;
  documentEnabled: boolean;
  /** Hex, as `generate_packet` takes it. */
  color: string;
  textSize: DividerTabTextSize;
  pageNumbers: boolean;
  position: PaginationPosition;
};

export const DEFAULT_PACKET_STYLE: PacketStyle = {
  mainGroupEnabled: defaultPacketConfig.divider.mainGroupEnabled,
  categoryEnabled: defaultPacketConfig.divider.categoryEnabled,
  documentEnabled: defaultPacketConfig.divider.documentEnabled,
  color: defaultPacketConfig.divider.color.toUpperCase(),
  textSize: defaultPacketConfig.divider.textSize,
  pageNumbers: defaultPacketConfig.pagination.enabled,
  position: defaultPacketConfig.pagination.position
};

export const PACKET_SWATCHES: readonly { name: string; hex: string }[] =
  defaultColors.map((c) => ({
    name: c.name.replace(/^Current /, ""),
    hex: c.value.toUpperCase()
  }));

export const TEXT_SIZE_LABELS: Record<DividerTabTextSize, string> = {
  small: "Small",
  normal: "Normal",
  large: "Large"
};

/** An uppercase "#RRGGBB", with or without the "#" typed; null for anything but six hex digits. */
export const parseHex = (value: string): string | null => {
  const match = /^#?([0-9a-f]{6})$/i.exec(value.trim());
  return match ? `#${match[1].toUpperCase()}` : null;
};

/** "Blue" for a preset, else the hex itself. */
export const colorName = (hex: string): string =>
  PACKET_SWATCHES.find((s) => s.hex === hex.toUpperCase())?.name ?? hex;

const listOf = (parts: string[]): string =>
  parts.length > 1
    ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`
    : parts[0] ?? "";

/** "before each folder and each sub-folder", or "none". */
export const dividerPlaces = (s: PacketStyle): string => {
  const parts = [
    s.mainGroupEnabled ? "each folder" : null,
    s.categoryEnabled ? "each sub-folder" : null,
    s.documentEnabled ? "each exhibit" : null
  ].filter((p): p is string => Boolean(p));
  return parts.length ? `before ${listOf(parts)}` : "none";
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** The firm's saved defaults as the controls edit them; CaseBlink's where a value is missing. */
export const packetStyleOf = (raw: unknown): PacketStyle => {
  const divider = isRecord(raw) && isRecord(raw.divider) ? raw.divider : {};
  const pagination =
    isRecord(raw) && isRecord(raw.pagination) ? raw.pagination : {};
  const bool = (v: unknown, fallback: boolean) =>
    typeof v === "boolean" ? v : fallback;
  return {
    mainGroupEnabled: bool(
      divider.mainGroupEnabled,
      DEFAULT_PACKET_STYLE.mainGroupEnabled
    ),
    categoryEnabled: bool(
      divider.categoryEnabled,
      DEFAULT_PACKET_STYLE.categoryEnabled
    ),
    documentEnabled: bool(
      divider.documentEnabled,
      DEFAULT_PACKET_STYLE.documentEnabled
    ),
    color:
      (typeof divider.color === "string" ? parseHex(divider.color) : null) ??
      DEFAULT_PACKET_STYLE.color,
    textSize:
      typeof divider.textSize === "string" &&
      divider.textSize in TEXT_SIZE_LABELS
        ? (divider.textSize as DividerTabTextSize)
        : DEFAULT_PACKET_STYLE.textSize,
    pageNumbers: bool(pagination.enabled, DEFAULT_PACKET_STYLE.pageNumbers),
    position:
      typeof pagination.position === "string" &&
      pagination.position in paginationPositionLabels
        ? (pagination.position as PaginationPosition)
        : DEFAULT_PACKET_STYLE.position
  };
};

/** What `default_packet_settings` saves, and what `generate_packet` takes. */
export const packetSettingsOf = (
  s: PacketStyle,
  contents?: PacketContents | null,
  order?: readonly PacketContentKind[] | null
): FirmPacketSettings => {
  const stored = contents ? storedPacketContents(contents) : undefined;
  const storedOrder = order ? storedPacketOrder(order) : undefined;
  return {
    divider: {
      mainGroupEnabled: s.mainGroupEnabled,
      categoryEnabled: s.categoryEnabled,
      documentEnabled: s.documentEnabled,
      color: s.color,
      textSize: s.textSize
    },
    pagination: {
      enabled: s.pageNumbers,
      position: s.position,
      startOn: "firstPage"
    },
    ...(stored ? { contents: stored } : {}),
    ...(storedOrder ? { contents_order: storedOrder } : {})
  };
};

/** "dividers before each folder, Blue tabs, numbers bottom right". */
export const packetSummary = (s: PacketStyle): string =>
  `dividers ${dividerPlaces(s)}, ${colorName(s.color)} tabs, ${
    s.pageNumbers
      ? `numbers ${paginationPositionLabels[s.position].toLowerCase()}`
      : "no page numbers"
  }`;

export const samePacketStyle = (a: PacketStyle, b: PacketStyle): boolean =>
  (Object.keys(a) as (keyof PacketStyle)[]).every((k) => a[k] === b[k]);
